import { Link } from "react-router-dom";
import { Medal, Lightning, MapPin, Headset, Recycle, Star, ShieldCheck, CheckCircle, Certificate, ArrowRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { WHY_US } from "@/data/site";

const ICONS = { Medal, Lightning, MapPin, Headset, Recycle, Star };

const FOUNDERS = [
  { name: "James Bettoni", role: "Founder & CEO", bio: "James founded Ebikes&Gear from a single Canterbury workshop with one belief: premium electric bikes, honestly sold and supported for life. He leads the brand, buying and rider-first culture across the UK & EU." },
  { name: "Harrison McGuire", role: "Co-Founder & Head of Workshop", bio: "Certified e-bike technician and lifelong rider, Harrison runs the Kent workshop — every bike that leaves the door is hand-built, torqued and road-tested under his watch." },
];

const CERTS = ["Bosch eBike Certified", "Shimano STEPS Approved", "Cytech Accredited", "Sold Secure Partner", "ISO 9001 Quality", "B Corp Pending"];

export default function About() {
  return (
    <>
      <SEO title="About Us — Europe's Premium eBike Experts" description="Discover the story behind Ebikes&Gear. 20+ years of expertise, certified technicians and thousands of happy riders across Europe." path="/about" />
      <PageHero
        title="We're on a mission to electrify Europe's streets"
        subtitle="For over two decades we've helped riders across the continent discover the freedom, joy and sustainability of premium electric bikes."
        image="/images/site/team.jpg"
        crumbs={[{ label: "About" }]}
      />

      {/* Story */}
      <section className="container-px py-16 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <img src="/images/site/team.jpg" alt="James Bettoni and Harrison McGuire outside the Ebikes&Gear store" className="rounded-3xl object-cover shadow-soft" />
          </Reveal>
          <Reveal>
            <p className="overline">Our story</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">From a single Kent workshop to all of Europe</h2>
            <div className="mt-5 space-y-4 text-muted-foreground leading-relaxed">
              <p>Ebikes&Gear started in a small Canterbury workshop with one simple belief: electric bikes should be premium, reliable and genuinely joyful to ride. No gimmicks, no cut corners.</p>
              <p>Today we're one of Europe's most trusted ebike specialists, shipping to riders in over 12 countries. But our obsession hasn't changed — we hand-check every bike, partner only with the best brands, and support our riders for life.</p>
              <p>Whether you're commuting through the city, conquering alpine trails or replacing your second car with a cargo bike, our experts are here to find you the perfect ride.</p>
            </div>
            <Link to="/shop" className="btn-primary mt-7">Explore our bikes <ArrowRight size={18} /></Link>
          </Reveal>
        </div>
      </section>

      {/* EEAT — Why us */}
      <section className="bg-card border-y border-border py-16 lg:py-24">
        <div className="container-px">
          <Reveal className="max-w-2xl"><p className="overline">Why riders trust us</p><h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Experience. Expertise. Trust.</h2></Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w) => { const I = ICONS[w.icon]; return (
              <StaggerItem key={w.title}>
                <div className="flex h-full gap-4 rounded-3xl border border-border bg-background p-6">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><I size={24} weight="bold" /></span>
                  <div><h3 className="text-lg font-bold">{w.title}</h3><p className="mt-1 text-sm text-muted-foreground">{w.text}</p></div>
                </div>
              </StaggerItem>
            ); })}
          </Stagger>
        </div>
      </section>

      {/* Founders */}
      <section className="container-px py-16 lg:py-24">
        <Reveal className="text-center"><p className="overline">Meet the founders</p><h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">The people behind your ride</h2></Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <img src="/images/site/team.jpg" alt="Founders James Bettoni and Harrison McGuire delivering a new e-bike outside the Ebikes&Gear Canterbury store" className="w-full rounded-3xl border border-border object-cover shadow-soft" data-testid="founders-photo" />
          </Reveal>
          <div className="grid gap-6">
            {FOUNDERS.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.07}>
                <div className="rounded-3xl border border-border bg-card p-7" data-testid={`founder-card-${i}`}>
                  <h3 className="text-xl font-bold">{e.name}</h3>
                  <p className="mt-0.5 text-sm font-semibold text-primary">{e.role}</p>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{e.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications / trust */}
      <section className="bg-card border-t border-border py-16">
        <div className="container-px">
          <Reveal className="text-center"><p className="overline flex items-center justify-center gap-2"><Certificate size={16} /> Accreditations</p><h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">Certified, accredited and trusted</h2></Reveal>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {CERTS.map((c) => (
              <div key={c} className="flex items-center gap-2 rounded-2xl border border-border bg-background p-4 text-sm font-semibold">
                <ShieldCheck size={20} className="text-primary shrink-0" /> {c}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
